import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { TimelineEvent, TimelineItem } from '../types';
import { styles as componentStyles } from '../view/timelineGroupStyles.scss';
import { ifDefined } from 'lit/directives/if-defined.js';

/**
 * @summary This component represents a group of timeline items, which can be expanded or collapsed.
 * @property {TimelineItem[]} items - The array of timeline items within this group.
 * @tag gz-collapsible-timeline-group
 */
@customElement('gz-collapsible-timeline-group')
export class GzCollapsibleTimelineGroupWebComponent extends LitElement {
  static get styles() {
    return [unsafeCSS(`${componentStyles}`)];
  }

  /**
   * List of timeline items in the group
   */
  @property({ type: Array })
  items: TimelineItem[] = [];

  // Tracks which item's panel is open. `null` means none.
  @property({ type: String })
  openPanelId: string | null = null;

  constructor() {
    super();
    // Listen for custom events from child items
    this.addEventListener(TimelineEvent.TOGGLE_ITEM, this.onToggleItem as EventListener);
  }

  private onToggleItem(e: CustomEvent<{ id: string }>): void {
    const itemId = e.detail.id;
    const wasOpen = this.openPanelId !== null;
    this.openPanelId = this.openPanelId === itemId ? null : itemId;
    if (this.openPanelId || wasOpen) {
      this.dispatchEvent(
        new CustomEvent(TimelineEvent.TOGGLE_ITEM_IN_GROUP, {
          detail: { id: itemId, isOpen: this.openPanelId !== null },
        })
      );
    }
  }

  render() {
    return html`
      ${repeat(
        this.items,
        item => item.id,
        item => {
          // Compute whether this item is open
          const { id, label, details, isEmphasized, groupedEntriesCount, suffixRenderer, contentRenderer } = item;
          const isOpen = this.openPanelId === id;
          return html`
            <gz-collapsible-timeline-item
              .id=${id}
              .label=${label}
              .details=${details}
              .suffixRenderer=${suffixRenderer}
              .contentRenderer=${contentRenderer}
              ?isOpen=${isOpen}
              ?isEmphasized=${isEmphasized}
              .groupedEntriesCount=${ifDefined(groupedEntriesCount)}
            ></gz-collapsible-timeline-item>
          `;
        }
      )}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-collapsible-timeline-group': GzCollapsibleTimelineGroupWebComponent;
  }
}
